import React, { useCallback, useMemo } from 'react';
import moment from 'moment';

import Inputs from 'components/Inputs';
import Typography from 'kingpin/atoms/Typography';
import { LabelDiv } from 'screens/Boards/PerformanceBoardForm';
import Row from 'components/Common/Row';
import useSlideoutRollingDateScope from 'components/PerformanceBoards/hooks/useSlideoutRollingDateScope';
import { getLabel } from 'components/DateInput/RelativeInput/RelativeDateBuilder';
import DateFieldPicker from 'components/Goals/GeneralGoalForm/KpiForm/DateFieldPicker';

// Calendar units only — bonus/fiscal periods are handled by the period-anchored
// end, not the lookback.
const CALENDAR_INTERVALS: ExtendedInterval[] = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
];

const getDateRangeText = (dateRange: DateRangeInput | undefined) => {
  if (!dateRange) {
    return '...';
  }
  const startText = moment(dateRange.startDate).format('MM/DD/YY');
  const endText = moment(dateRange.endDate).format('MM/DD/YY');
  return `${startText} - ${endText}`;
};

const RollingDateInput = ({
  dateFilter,
  setLookback,
  dateField,
  setDateField,
  dataset,
}: {
  dateFilter: PerformanceBoardTypes.SlideOut.RollingDateFilter;
  setLookback: (next: PerformanceBoardTypes.SlideOut.RollingLookback) => void;
  dateField?: string;
  setDateField: React.Dispatch<React.SetStateAction<string | undefined>>;
  dataset?: string;
}) => {
  const { lookback } = dateFilter;
  const dateScope = useSlideoutRollingDateScope(dateFilter);

  const onNumberOfIntervalsChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newNumber = event.target.value;
      if (!newNumber) {
        return;
      }
      const parsed = Number(newNumber);
      setLookback({
        type: 'last',
        interval: lookback.interval,
        n: parsed < 1 ? 1 : parsed,
      });
    },
    [lookback.interval, setLookback],
  );

  const intervalOptions = useMemo(
    (): DropdownOption[] =>
      CALENDAR_INTERVALS.map((interval) => ({
        label: getLabel({ ...lookback, currentToDate: false }, interval),
        isSelected: lookback.interval === interval,
        onSelected: () => {
          setLookback({ type: 'last', interval, n: lookback.n });
        },
      })),
    [lookback, setLookback],
  );

  return (
    <>
      <LabelDiv>
        <Typography.Body type="Label">Show the last</Typography.Body>
      </LabelDiv>
      <Row style={{ marginBottom: 8 }}>
        <Inputs.TextInput
          data-testid="rolling-n"
          type="number"
          value={lookback.n ? lookback.n.toString(10) : ''}
          state={lookback.n === undefined ? 'Error' : undefined}
          onChange={onNumberOfIntervalsChanged}
        />
        <div style={{ marginLeft: 8, minWidth: 132 }}>
          <Inputs.Dropdown
            options={intervalOptions}
            selectedLabel={getLabel(
              { ...lookback, currentToDate: false },
              lookback.interval,
            )}
            unsetWidth
          />
        </div>
      </Row>
      {dataset && (
        <div style={{ marginBottom: 8 }}>
          <LabelDiv>
            <Typography.Body type="Label">Query Date</Typography.Body>
          </LabelDiv>
          <DateFieldPicker
            dateField={dateField}
            setDateField={setDateField}
            dataTypes={[dataset]}
          />
        </div>
      )}
      <Typography.Body type="Body 12">
        Ends at the end of the selected bonus period (or today if the period is
        in progress).
      </Typography.Body>
      <Typography.Body type="Body 12">
        {`Selected range as of today: ${getDateRangeText(dateScope)}`}
      </Typography.Body>
    </>
  );
};

export default RollingDateInput;
